import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { EscrowAccountsService } from './escrow-accounts.service';
import { forkJoin } from 'rxjs';
import { matchModeOptionsDateList } from 'src/app/utils/filterDropDownData';
import { helperService } from 'src/app/utils/helper';

@Component({
    selector: 'escrow-accounts',
    templateUrl: './escrow-accounts.component.html',
    styleUrls: ['./escrow-accounts.component.scss'],
    standalone: false
})
export class EscrowAccountsComponent implements OnInit {
  escrowAccounts: any[] = [];
  cols: any[] = [];
  escrowDialog = false;
  viewOnly = false;
  newEscrow = false;
  title = '';
  loading = true;
  totalRecords = 0;
  first = 1;
  submitted = false;
  btnLoading = false;
  lastTableLazyLoadEvent: any = null;

  escrowForm: UntypedFormGroup;
  defaultEscrowForm: any = {};

  statusList = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
  ];
  matchModeOptionsDate: SelectItem[]=matchModeOptionsDateList;

  private escrowAccountsSubscription: any = null;

  filterDatesMatchModes:any={createdOn:'dateIs'};
  filterDates:any={createdOn:null};
  dateFilters:any=[];

  constructor(
    private escrowService: EscrowAccountsService,
    private readonly messageService: MessageService,
    private helperService: helperService,
    private cdr: ChangeDetectorRef
  ) {
    this.escrowForm = new UntypedFormGroup({
      id: new UntypedFormControl(''),
      accountName: new UntypedFormControl('', [Validators.required]),
      account: new UntypedFormControl('', [Validators.required]),
      active: new UntypedFormControl('Active'),
      createdBy: new UntypedFormControl(''),
      createdOn: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.initTableCols();
    this.defaultEscrowForm = this.escrowForm.value;
    this.loading = true;
    this.loadEscrowAccounts({ first: 1, rows: 10 });
  }

  private initTableCols() {
    this.cols = [
      { field: 'accountName', header: 'Account Name' },
      { field: 'account', header: 'Account' },
      { field: 'active', header: 'Status' },
      { field: 'createdBy', header: 'Created By' },
      { field: 'createdOn', header: 'Created On' },
    ];
  }

  onDateFilterChange(field: any) {
    if(this.filterDatesMatchModes[field]=='betweenDate'){
      this.filterDates[field] =null ;
    }
  }

  clearDatesFilter(field: any) {
    this.filterDates[field] = null;
    this.dateFilters =this.dateFilters.filter((filter:any) => filter.field !== field);
    this.loadEscrowAccounts(this.lastTableLazyLoadEvent);
  }

  applyDatesFilter(field: any) {
    let dates=this.filterDates[field]
    const index = this.dateFilters.findIndex((filter:any) => filter.field === field);
    if(index>-1){
      this.dateFilters.splice(index,1);
    }
    if(this.filterDatesMatchModes[field]=='betweenDate'){
      this.dateFilters.push({field:field, operator: "betweenDate", value:  this.convertDateToFormattedPayloadStr(dates[0]),
                            valueTo:this.convertDateToFormattedPayloadStr(dates[1])});
    }else{
      this.dateFilters.push({field:field, operator: this.filterDatesMatchModes[field], value:  this.convertDateToFormattedPayloadStr(dates)});
    }
    this.loadEscrowAccounts(this.lastTableLazyLoadEvent);
  }

  convertDateToFormattedPayloadStr(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  loadEscrowAccounts(event: any) {
    this.loading = true;
    if (this.escrowAccountsSubscription) {
      this.escrowAccountsSubscription.unsubscribe();
    }
    event = event ? event : this.lastTableLazyLoadEvent || { first: 1, rows: 10 };
    this.lastTableLazyLoadEvent = event;
    this.loading = true;
    event.first = event.first || 1;
    event.rows = event.rows || 10;
    // Build payload for filters and sorting, similar to catalogue
    const payload = this.helperService.generateEventPayload(event);
    if(this.dateFilters){
      payload.filters=payload.filters.concat(this.dateFilters)
    }
    this.escrowAccountsSubscription = this.escrowService.getEscrowAccounts(Math.floor(event.first / event.rows + 1), event.rows, payload).subscribe(
      (res) => {
        this.escrowAccounts = res.data || [];
        this.totalRecords = res.customValues?.totalNumberOfRecords || 0;
        this.loading = false;
      },
      (err) => {
        this.escrowAccounts = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    );
  }

  addEscrowAccount() {
    this.escrowForm.reset(this.defaultEscrowForm);
    this.newEscrow = true;
    this.viewOnly = false;
    this.title = 'Add Escrow Account';
    this.escrowDialog = true;
  }

  editEscrowAccount(account: any) {
    this.escrowForm.patchValue(account);
    this.newEscrow = false;
    this.viewOnly = false;
    this.title = 'Edit Escrow Account';
    this.escrowDialog = true;
  }

  viewEscrowAccount(account: any) {
    this.escrowForm.patchValue(account);
    this.escrowForm.disable();
    this.viewOnly = true;
    this.title = 'View Escrow Account';
    this.escrowDialog = true;
  }

  saveEscrowAccount(toggle: boolean = false) {
    this.submitted = true;
    if (this.escrowForm.invalid) return;
    this.btnLoading = true;
    const { accountName, account, active, id } = this.escrowForm.value;
    const payload: {
      accountName: string,
      account: string,
      active: string
    } = { accountName, account, active };

    if(toggle){
      payload.active = this.escrowForm.value.active === 'Active' ? 'Inactive' : 'Active';
    }
    this.escrowService.saveEscrowAccount(payload, id).subscribe(
      (res) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Escrow Account saved.' });
        this.escrowDialog = false;
        this.btnLoading = false;
        this.loadEscrowAccounts(this.lastTableLazyLoadEvent || { first: 1, rows: 10 });
      },
      (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save.' });
        this.btnLoading = false;
      }
    );
  }

  deleteEscrowAccount(account: any) {
    this.loading = true;
    this.escrowService.deleteEscrowAccount(account.id).subscribe(
      (res) => {
        // this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Account activated/inactivated.' });
        this.loadEscrowAccounts(this.lastTableLazyLoadEvent || { first: 1, rows: 10 });
      },
      (err) => {
        this.loading = false;
        // this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to activate/inactivate.' });
      }
    );
  }
  toggleEscrowStatus(actObj: any) {
     const payload: {
      accountName: string,
      account: string,
      active: string
    } = { accountName: actObj.accountName, account: actObj.account, active: actObj.active };
    payload.active = payload.active === 'Active' ? 'Inactive' : 'Active';

    this.loading = true;
    this.escrowService.saveEscrowAccount(payload, actObj.id).subscribe(
      (res) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Escrow Account ${payload.active === 'Active' ? 'deactivated' : 'activated'} successfully.` });
        this.loadEscrowAccounts(this.lastTableLazyLoadEvent || { first: 1, rows: 10 });
      },
      (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to toggle status.' });
      }
    );
  }

  closeDialog() {
    this.escrowDialog = false;
    this.submitted = false;
    this.escrowForm.reset(this.defaultEscrowForm);
  }
}
